import { useState, useEffect } from 'react';
import { Repeat, Plus, Trash2, Play, Pause, RefreshCw } from 'lucide-react';
import { PageShell, Modal, FG, INP, SEL, Row2, BtnPrimary, BtnGhost, BtnDanger, Spinner, fmtINR, fmtDate, useBanner, useWalletUser, EmptyState } from './WalletShared';
import walletController from '../../controllers/walletController';

const RecurringPayments = () => {
  const { userId } = useWalletUser();
  const { banner, notify, clearBanner } = useBanner();
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showAdd,  setShowAdd]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [toggling, setToggling] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form,     setForm]     = useState({ name:'', amount:'', frequency:'monthly', method:'upi', startDate:'', description:'' });
  const f = (k,v) => setForm(p => ({ ...p, [k]:v }));

  const load = async () => {
    setLoading(true);
    const res = await walletController.getRecurring(userId);
    if (res.success) setItems(res.data);
    else notify('error', res.message);
    setLoading(false);
  };
  useEffect(() => { load(); }, [userId]);

  const handleAdd = async () => {
    if (!form.name||!form.amount||!form.startDate) return notify('error','Name, amount and start date are required');
    setSaving(true);
    const res = await walletController.createRecurring(userId, { ...form, amount:Number(form.amount) });
    setSaving(false);
    if (res.success) { notify('success', res.message); setShowAdd(false); load(); }
    else notify('error', res.message);
  };

  const handleToggle = async (id, current) => {
    setToggling(id);
    const res = await walletController.toggleRecurring(userId, id, !current);
    setToggling(null);
    if (res.success) { notify('success', res.message); load(); }
    else notify('error', res.message);
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    const res = await walletController.deleteRecurring(userId, id);
    setDeleting(null);
    if (res.success) { notify('success', res.message); load(); }
    else notify('error', res.message);
  };

  const FREQ_LABELS = { daily:'Daily', weekly:'Weekly', monthly:'Monthly', quarterly:'Quarterly', yearly:'Yearly' };

  return (
    <PageShell title="Recurring Payments" subtitle="Automated payment schedules" icon={Repeat}
      banner={banner} onCloseBanner={clearBanner}
      actions={<button onClick={() => setShowAdd(true)} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 14px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' }}><Plus size={14}/>Add Recurring</button>}>

      {loading ? <div style={{ display:'flex', justifyContent:'center', padding:'50px 0', gap:10, color:'#64748b' }}><Spinner size={24}/></div>
        : items.length===0 ? <EmptyState icon={Repeat} message="No recurring payments set up" action={<button onClick={() => setShowAdd(true)} style={{ padding:'8px 18px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:13, cursor:'pointer' }}>Set Up Recurring</button>}/>
        : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
            {items.map(item => (
              <div key={item._id} style={{ background:'#fff', borderRadius:12, border:'1px solid #f1f5f9', padding:'16px 18px', opacity:item.isActive?1:0.65 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:'#1e293b' }}>{item.name}</div>
                    <div style={{ fontSize:11, color:'#94a3b8', textTransform:'capitalize' }}>{FREQ_LABELS[item.frequency]||item.frequency}</div>
                  </div>
                  <span style={{ fontSize:10, padding:'2px 8px', borderRadius:6, fontWeight:600, background:item.isActive?'#dcfce7':'#f1f5f9', color:item.isActive?'#15803d':'#64748b' }}>
                    {item.isActive?'Active':'Paused'}
                  </span>
                </div>
                <div style={{ fontSize:20, fontWeight:700, color:'#1e293b', marginBottom:8 }}>{fmtINR(item.amount)}</div>
                <div style={{ fontSize:11, color:'#94a3b8', marginBottom:12 }}>Next: {fmtDate(item.nextDate)} · via {item.method?.replace('_',' ').toUpperCase()}</div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => handleToggle(item._id, item.isActive)} disabled={toggling===item._id}
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:5, padding:'7px', background:item.isActive?'#fef9f0':'#f0fdf4', border:`1px solid ${item.isActive?'#fde68a':'#86efac'}`, borderRadius:7, fontSize:12, cursor:'pointer', color:item.isActive?'#92400e':'#15803d', fontWeight:500 }}>
                    {toggling===item._id ? <Spinner size={12}/> : item.isActive ? <><Pause size={12}/>Pause</> : <><Play size={12}/>Resume</>}
                  </button>
                  <button onClick={() => handleDelete(item._id)} disabled={deleting===item._id}
                    style={{ padding:'7px 10px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:7, cursor:'pointer', color:'#ef4444', display:'flex', alignItems:'center' }}>
                    {deleting===item._id ? <Spinner size={12}/> : <Trash2 size={12}/>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      {showAdd && (
        <Modal title="New Recurring Payment" onClose={() => setShowAdd(false)}
          footer={<><BtnGhost onClick={() => setShowAdd(false)}>Cancel</BtnGhost><BtnPrimary onClick={handleAdd} disabled={saving}>{saving?<><Spinner size={13}/>Creating…</>:<><Plus size={13}/>Create</>}</BtnPrimary></>}>
          <Row2>
            <FG label="Name *"><INP value={form.name} onChange={e => f('name',e.target.value)} placeholder="e.g. Netflix, Rent"/></FG>
            <FG label="Amount (₹) *"><INP type="number" min="1" value={form.amount} onChange={e => f('amount',e.target.value)}/></FG>
          </Row2>
          <Row2>
            <FG label="Frequency">
              <SEL value={form.frequency} onChange={e => f('frequency',e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </SEL>
            </FG>
            <FG label="Payment Method">
              <SEL value={form.method} onChange={e => f('method',e.target.value)}>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="wallet">Wallet</option>
              </SEL>
            </FG>
          </Row2>
          <FG label="Start Date *"><INP type="date" value={form.startDate} onChange={e => f('startDate',e.target.value)}/></FG>
          <FG label="Description"><INP value={form.description} onChange={e => f('description',e.target.value)} placeholder="Optional"/></FG>
        </Modal>
      )}
    </PageShell>
  );
};
export default RecurringPayments;